// SpaceSentinelInsights.jsx
import React, { useState, useMemo } from "react";

const STAKEHOLDERS = [
  {
    id: "learners",
    label: "Learners & Students",
    emoji: "👨‍🎓",
    pains: [
      "Limited access to immersive space simulations and real-world data.",
      "Isolation in self-paced learning without peer interaction.",
      "Outdated or fragmented educational resources on emerging tech.",
      "Difficulty maintaining motivation for long-term space studies.",
      "Lack of personalized guidance for career paths in aerospace.",
    ],
    opportunities: [
      "Immersive Book Platform & Monthly Space Magazine for engaging content.",
      "Quizzes, Games, and AI Counselling for interactive skill-building.",
      "Community Connects for peer mentorship and motivation boosts.",
    ],
  },
  {
    id: "educators",
    label: "Educators & Teachers",
    emoji: "👩‍🏫",
    pains: [
      "Gaps in curriculum for cutting-edge space topics like defense tech.",
      "Scarcity of interactive tools for classroom engagement.",
      "Challenges in assessing student progress in complex subjects.",
      "Limited professional development on immersive edtech.",
      "Difficulty connecting with industry experts for guest insights.",
    ],
    opportunities: [
      "Counsellors/Teachers Contact & Community Presentations for collaborations.",
      "Monthly Digital Magazine contributions and POV sharing.",
      "AI Counselling tools to personalize teaching and feedback.",
    ],
  },
  {
    id: "enthusiasts",
    label: "Space Enthusiasts",
    emoji: "🔭",
    pains: [
      "Information overload from scattered news and unverified sources.",
      "Lack of vibrant communities for sharing ideas and projects.",
      "Barriers to funding personal stargazing or DIY space projects.",
      "Skill gaps in applying space knowledge to real hobbies.",
      "Delayed access to bite-sized, timely cosmic updates.",
    ],
    opportunities: [
      "Short News like inShorts for quick cosmic bites.",
      "Community Connects & Project Marketplace for idea exchanges.",
      "Monthly Earnings Options and Prizes for enthusiast contributions.",
    ],
  },
  {
    id: "scientists",
    label: "Researchers & Scientists",
    emoji: "🔬",
    pains: [
      "Silos in data sharing across global space research teams.",
      "Challenges in collaborative brainstorming for breakthroughs.",
      "Time lost to manual literature reviews and trend spotting.",
      "Funding hurdles for innovative space experiments.",
      "Limited platforms to showcase preliminary findings publicly.",
    ],
    opportunities: [
      "POVs & Community Presentations for rapid peer reviews.",
      "Immersive Book Platform for deep-dive resource access.",
      "AI Counselling for hypothesis testing and data insights.",
    ],
  },
  {
    id: "engineers",
    label: "Engineers & Developers",
    emoji: "🛠️",
    pains: [
      "Inadequate simulation tools for prototyping space hardware.",
      "Supply chain issues for specialized materials and components.",
      "Coordination bottlenecks in multidisciplinary teams.",
      "Safety risks in virtual testing of defense-integrated designs.",
      "Lack of gamified training for emerging space tech skills.",
    ],
    opportunities: [
      "Project Marketplace for sourcing and collaborating on builds.",
      "Quizzes & Games for certification in astro-engineering.",
      "Defense Tech integrations via secure, immersive simulations.",
    ],
  },
  {
    id: "analysts",
    label: "Defense & Security Analysts",
    emoji: "🛡️",
    pains: [
      "Gaps in real-time threat intelligence from orbital assets.",
      "Insufficient immersive training for space-based scenarios.",
      "Secure collaboration barriers with international partners.",
      "Scalability issues in analyzing vast satellite data sets.",
      "Policy lags in adopting AI for predictive defense modeling.",
    ],
    opportunities: [
      "AI Counselling for scenario simulations and risk forecasting.",
      "Community Connects for classified POV exchanges.",
      "Rewards & Scholarships for innovative defense project ideas.",
    ],
  },
  {
    id: "leaders",
    label: "Community Leaders",
    emoji: "👥",
    pains: [
      "Declining engagement in virtual space interest groups.",
      "Inclusion challenges for diverse global participants.",
      "Event planning hurdles for online showcases and meetups.",
      "Feedback loops that don't translate to actionable improvements.",
      "Growth stalls due to lack of monetization for organizers.",
    ],
    opportunities: [
      "Community Connects & Presentations for seamless event hosting.",
      "Monthly Earnings via sponsored quizzes and games.",
      "Prizes & Rewards to incentivize active participation.",
    ],
  },
  {
    id: "funders",
    label: "Sponsors & Funders",
    emoji: "💰",
    pains: [
      "Difficulty measuring ROI on space education investments.",
      "Vetting challenges for high-potential learner projects.",
      "Tracking long-term impact of scholarships and grants.",
      "Risk assessment in volatile space tech landscapes.",
      "Limited outreach to untapped enthusiast networks.",
    ],
    opportunities: [
      "Project Marketplace analytics for investment scouting.",
      "Scholarships & Rewards tracking via integrated dashboards.",
      "POVs & Presentations for pitch-ready showcases.",
    ],
  },
  {
    id: "institutions",
    label: "Institutions & Governments",
    emoji: "🏛️",
    pains: [
      "Policy gaps in fostering space STEM pipelines.",
      "Talent shortages in defense and exploration sectors.",
      "Infrastructure limits for widespread immersive training.",
      "International collaboration hurdles on shared data.",
      "Aggregated insights lacking for national space strategies.",
    ],
    opportunities: [
      "Monthly Magazine & Short News for policy trend feeds.",
      "Community dashboards for talent scouting and certifications.",
      "AI Counselling integrations for scalable workforce development.",
    ],
  },
];

const USPs = [
  {
    id: "immersiveLearning",
    title: "Immersive Learning Platform",
    desc: "Dive into space via interactive books, monthly digital magazines, quizzes, and games for hands-on cosmic education.",
    emoji: "🚀📖",
  },
  {
    id: "galacticNetwork",
    title: "Galactic Community Network",
    desc: "LinkedIn-style connects, POVs, and showcase presentations to foster collaborations among explorers and innovators.",
    emoji: "🌌👥",
  },
  {
    id: "astroMarketplace",
    title: "Astro Earnings & Marketplace",
    desc: "Monetize projects through digital earning options, a vibrant marketplace, and reward-driven opportunities.",
    emoji: "🛒⭐",
  },
  {
    id: "starguardAI",
    title: "StarGuard AI Counselling",
    desc: "Personalized AI guidance alongside expert teachers for career navigation and skill enhancement in space domains.",
    emoji: "🤖🧠",
  },
  {
    id: "cosmicRewards",
    title: "Cosmic Rewards System",
    desc: "Prizes, scholarships, and gamified incentives to celebrate achievements and fuel ongoing exploration.",
    emoji: "🏆🌟",
  },
];

/* -------------------- Helper UI subcomponents -------------------- */

const Pill = ({ children }) => (
  <span className="inline-block bg-blue-700/40 text-blue-100 px-2 py-0.5 rounded-full text-xs font-medium">
    {children}
  </span>
);

const ExpandIcon = ({ open }) => (
  <span className={`ml-2 text-sm ${open ? "rotate-180" : "rotate-0"} transition-transform`}>
    ▼
  </span>
);

/* -------------------- Main Component -------------------- */

export default function SpaceSentinelInsights() {
  const [tab, setTab] = useState("stakeholders"); 
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const stakeholdersFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAKEHOLDERS;
    return STAKEHOLDERS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.pains.some((p) => p.toLowerCase().includes(q)) ||
        s.opportunities.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-blue-950 p-4 md:p-8 text-gray-100">
      {/* Header */}
      <header className="max-w-6xl mx-auto flex items-center justify-between gap-4 mb-6">
        <div className="flex flex-col">
          <div className="flex flex-col">
            <h1 className="text-left text-xl md:text-2xl font-bold tracking-tight text-blue-300">Think5Whys — SpaceSentinel Insights</h1>
            <p className="text-sm text-gray-300 mt-1">Opportunities & USPs for students cosmic explorers, educators and innovators.</p>
          </div>

          <div className="flex items-center gap-3">
            <Pill>Global: $650B economy in 2025 • 1M+ jobs</Pill><grok-card data-id="bc4645" data-type="citation_card"></grok-card>
            <Pill>Satellites: 10K+ orbiting • Missions: 100+</Pill>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="mx-auto mb-4 flex flex-col gap-2">
        <div className="flex flex-row">
          <button
            onClick={() => setTab("stakeholders")}
            className={`px-3 py-2 rounded-md font-semibold text-sm ${
              tab === "stakeholders" ? "bg-blue-600 text-black shadow" : "bg-gray-800 text-gray-200 hover:bg-gray-700"
            }`}
          >
            Galactic Stakeholders
          </button>
          <button
            onClick={() => setTab("usps")}
            className={`px-3 py-2 rounded-md font-semibold text-sm ${
              tab === "usps" ? "bg-blue-600 text-black shadow" : "bg-gray-800 text-gray-200 hover:bg-gray-700"
            }`}
          >
            Stellar USPs
          </button>
        </div>

        <div className="flex items-start gap-2">
          <input
            aria-label="Search stakeholders"
            placeholder="Search stakeholders, pain or opportunity..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="bg-gray-800 text-gray-200 placeholder-gray-400 px-3 py-2 rounded-md text-sm w-56 md:w-80 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => { setQuery(""); setExpandedId(null); }}
            className="text-sm bg-gray-700 px-3 py-2 rounded-md hover:bg-gray-600"
          >
            Reset
          </button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto space-y-6">
        {/* Stakeholders Tab */}
        {tab === "stakeholders" && (
          <>
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stakeholdersFiltered.map((s) => {
                const isOpen = expandedId === s.id;
                return (
                  <article key={s.id} className="bg-gradient-to-br from-gray-800 to-blue-900/30 rounded-lg border border-blue-800/30 shadow-sm overflow-hidden">
                    <div className="p-4 flex items-start gap-3">
                      <div className="text-3xl">{s.emoji}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="text-lg font-semibold text-blue-300">{s.label}</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-300">Stakeholder</span>
                            <button
                              onClick={() => setExpandedId(isOpen ? null : s.id)}
                              aria-expanded={isOpen}
                              className="ml-2 bg-blue-700/30 text-sm px-2 py-1 rounded"
                            >
                              {isOpen ? "Collapse" : "Expand"} <ExpandIcon open={isOpen} />
                            </button>
                          </div>
                        </div>

                        {/* Snapshot */}
                        <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                          <strong>Pain points:</strong> {s.pains[0]} • {s.pains[1]} • {s.pains[2]}
                        </p>

                        {/* Expanded details */}
                        {isOpen && (
                          <div className="mt-3 space-y-3 text-sm">
                            <div>
                              <h4 className="font-medium text-blue-300">Top 5 Pain Points</h4>
                              <ol className="list-decimal list-inside mt-1 space-y-1 text-gray-200">
                                {s.pains.map((p, idx) => (
                                  <li key={idx}>{p}</li>
                                ))}
                              </ol>
                            </div>

                            <div>
                              <h4 className="font-medium text-blue-300">Opportunities SpaceSentinel Can Deliver</h4>
                              <ul className="list-disc list-inside mt-1 space-y-1 text-gray-200">
                                {s.opportunities.map((o, idx) => (
                                  <li key={idx}>{o}</li>
                                ))}
                              </ul>
                            </div>

                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => alert(`${s.label} — Launch Diagnostics`)}
                                className="text-sm bg-blue-600 text-black px-3 py-1 rounded font-semibold"
                              >
                                Launch Diagnostics
                              </button>
                              <button
                                onClick={() => alert(`${s.label} — Orbit Action Plan`)}
                                className="text-sm bg-gray-700 px-3 py-1 rounded hover:bg-gray-600"
                              >
                                Orbit Action Plan
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </section>

            {stakeholdersFiltered.length === 0 && (
              <div className="bg-gray-800 p-4 rounded text-center text-gray-300">No results for your search. Try querying 'AI' or 'marketplace'.</div>
            )}
          </>
        )}

        {/* USPs Tab */}
        {tab === "usps" && (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USPs.map((u) => (
              <div key={u.id} className="bg-gradient-to-br from-blue-700/20 to-gray-800 rounded-lg p-4 border border-blue-600/20">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{u.emoji}</div>
                  <div>
                    <h3 className="font-semibold text-lg text-blue-300">{u.title}</h3>
                    <p className="text-sm text-gray-200 mt-2">{u.desc}</p>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => alert(`Explore: ${u.title} in SpaceSentinel`)}
                    className="bg-blue-600 text-black px-3 py-2 rounded font-semibold text-sm"
                  >
                    Explore
                  </button>
                  <button
                    onClick={() => alert(`Demo: ${u.title} Features`)}
                    className="bg-gray-800 px-3 py-2 rounded text-gray-200 hover:bg-gray-700 text-sm"
                  >
                    Demo
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto mt-8 text-center text-sm text-gray-400">
        Think5Whys • SpaceSentinel Insights • Cosmic snapshot — built for stellar prototyping. 🌌
      </footer>
    </div>
  );
}